<template>
  <component
    :is="tagName"
    v-if="localShow"
    :class="currentClass"
  >
    <o-icon
      v-if="currentIcon"
      :icon="currentIcon"
      :class="accentClasses"
      fill
      class="mr-4"
      :size="18"
    />
    <div class="w-10 flex-shrink flex-grow">
      <h5 v-if="title" class="pt-1 font-medium text-current leading-none" :class="{ 'mb-2' : $slots.default }" v-html="title" />
      <div v-if="$slots.default || content">
        <slot>
          <p class="mt-2">
            {{ content }}
          </p>
        </slot>
      </div>
    </div>
    <span
      v-if="dismissible"
      ref="close"
      :class="closeButtonClass"
      @click="hide"
    >
      <slot name="close">
        <svg
          :class="closeIconClass"
          role="button"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <title>{{ closeButtonTitle }}</title>
          <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" /></svg>
      </slot>
    </span>
  </component>
</template>

<script lang="ts">
export default { name: 'OAlert' }
</script>

<script setup lang="ts">
interface Props {
  tagName?: string
  variant?: 'danger' | 'warning' | 'success' | 'info' | 'primary' | null
  dismissible?: boolean
  show?: boolean
  timeout?: number | null
  closeButtonTitle?: string
  title?: string | null
  content?: string | null
  baseClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  tagName: 'div',
  variant: null,
  dismissible: false,
  show: false,
  timeout: null,
  closeButtonTitle: 'Close',
  title: null,
  content: null,
  baseClass: 'border rounded p-4 relative flex items-start relative',
})

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'shown'): void
  (e: 'hidden'): void
}>()

const localShow = ref(props.show)

const defaultClass = 'bg-gray-100 dark:bg-gray-900/20 border-gray-500 text-gray-700 dark:text-white dark:border-gray-900/50'
const primaryClass = 'bg-primary-100 dark:bg-primary-950/20 border-primary-300 text-primary-700 dark:text-white dark:border-primary-900/50'
const infoClass = 'bg-blue-100 dark:bg-blue-950/20 border-blue-300 text-blue-700 dark:text-white dark:border-blue-900/50'
const successClass = 'bg-green-100 dark:bg-green-950/20 border-green-300 text-green-700 dark:text-white dark:border-green-900/50'
const dangerClass = 'bg-red-100 dark:bg-red-950/20 border-red-300 text-red-700 dark:text-white dark:border-red-900/50'
const warningClass = 'bg-yellow-100 dark:bg-yellow-950/20 border-yellow-300 text-yellow-700 dark:text-white dark:border-yellow-900/50'

const accentBaseClass = 'pt-px'
const defaultAccentClass = 'text-gray-500 dark:text-gray-400'
const primaryAccentClass = 'text-primary-500 dark:text-primary-400'
const infoAccentClass = 'text-blue-500 dark:text-blue-400'
const successAccentClass = 'text-green-500 dark:text-green-400'
const dangerAccentClass = 'text-red-500 dark:text-red-400'
const warningAccentClass = 'text-yellow-500 dark:text-yellow-400'

const closeButtonClass = 'absolute top-0 bottom-0 right-0 px-4 py-3'
const closeIconClass = 'fill-current h-6 w-6'

const statusClasses = computed(() => {
  const classes: string[] = []
  switch (props.variant) {
    case 'primary':
      classes.push(primaryClass)
      break
    case 'info':
      classes.push(infoClass)
      break
    case 'danger':
      classes.push(dangerClass)
      break
    case 'success':
      classes.push(successClass)
      break
    case 'warning':
      classes.push(warningClass)
      break
    default:
      classes.push(defaultClass)
  }
  return classes
})

const accentClasses = computed(() => {
  const classes = [accentBaseClass]
  switch (props.variant) {
    case 'primary':
      classes.push(primaryAccentClass)
      break
    case 'info':
      classes.push(infoAccentClass)
      break
    case 'danger':
      classes.push(dangerAccentClass)
      break
    case 'success':
      classes.push(successAccentClass)
      break
    case 'warning':
      classes.push(warningAccentClass)
      break
    default:
      classes.push(defaultAccentClass)
  }
  return classes
})

const currentClass = computed(() => {
  const classes = [
    'o-alert',
    `o-alert-variant-${props.variant || 'default'}`,
  ]
  if (props.baseClass) {
    classes.push(props.baseClass)
  }
  return classes.concat(statusClasses.value)
})

const currentIcon = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'infoBold'
    case 'info':
      return 'infoBold'
    case 'danger':
      return 'dangerBold'
    case 'success':
      return 'successBold'
    case 'warning':
      return 'warningBold'
  }
  return false
})

function initTimeout() {
  setTimeout(() => {
    hide()
  }, props.timeout!)
}

function hide() {
  localShow.value = false
}

watch(() => props.show, (show) => {
  localShow.value = show
})

watch(localShow, (val) => {
  emit('update:show', val)
  if (val) {
    emit('shown')
    if (props.timeout) {
      initTimeout()
    }
  } else {
    emit('hidden')
  }
})

onMounted(() => {
  if (localShow.value && props.timeout) {
    initTimeout()
  }
})
</script>
